import { defineStore } from 'pinia'
import { PreferredCoinsReward, type State, type TripType } from './userStore.types'
import { useApi } from '@/shared/composables'
import {
  ADULTS_COUNT_KEY,
  CHILDREN_COUNT_KEY,
  CIVIL_ID_EXPIRY_DATE_KEY,
  COUNTRY_CODE_KEY,
  DATE_OF_BIRTH_KEY,
  DEPARTURE_DATE_KEY,
  DESTINATION_KEY,
  EMAIL_KEY,
  EMIRATES_ID_KEY,
  FAMILY_NAME_KEY,
  FIRST_NAME_KEY,
  IS_AFFINITY,
  IS_HERO_SECTION,
  LAST_FLOW_DIRECTION_KEY,
  MASKED_PHONE_KEY,
  NATIONALITY_KEY,
  PASSPORT_NUMBER_KEY,
  PAYMENT_METHOD,
  PHONE_KEY,
  PREV_TRIP_TYPE_KEY,
  PROMOCODE_KEY,
  REDIRECT_URL,
  RESIDENCE_COUNTRY_KEY,
  RETURN_DATE_KEY,
  SELECTED_PLAN_KEY,
  TRIP_TYPE_KEY,
  WITH_COVID_KEY,
  WITH_CYBER_KEY,
  WITH_WINTER_SPORT_KEY,
  GENDER_KEY,
  REQUIRED_FIELDS,
  PERSONAL_FIELDS,
  OUTBOUND_PERSONAL_FIELDS,
  PREFERRED_REWARD_KEY,
} from './userStore.constants'
import type { Country, CountryBoundType, Nationality } from '@/shared/types/api.types'
import { TravelDirection } from '@/shared/types/common.types'
import { useCacheService } from '@/shared/composables/useCacheService'
import { TRAVEL_KEY } from '@/shared/constants/storage.constants'
import { parseDateFromStr } from '@/shared/utils'
import { applyPhoneMask } from '@/shared/utils/Masks'
import type { FormDataResponse } from '@/shared/api/services/UserService/UserService.types'
/*
TODO: Try to move function for update value in locale storage into the separate file.
function updateValue(withLocalStorage, key, value) {
    const storage = useLocalStorage()

    if (withLocalStorage){
        storage.setOrUpdate(key, value)
    }
}
*/

const isValidDate = (date: Date): boolean => {
  const todayDate = new Date()
  return date >= todayDate
}

const sessionStorageCacheService = useCacheService('session', '')
const localStorageCacheService = useCacheService('local', '')

const getCacheState = () => ({
  ...localStorageCacheService.getFromStorage(TRAVEL_KEY),
  ...sessionStorageCacheService.getFromStorage(TRAVEL_KEY),
})

const getInfo = (state: State, direction: TravelDirection) => {
  const personalInfo: Record<string, string | Nationality | null> = {
    firstName: String(state.firstName || ''),
    familyName: String(state.familyName || ''),
    email: String(state.email || ''),
    phone: String(state.phone || ''),
    dateOfBirth: String(state.dateOfBirth || ''),
    nationality: state.nationality || null,
    passportNumber: String(state.passportNumber || ''),
    gender: String(state.gender || ''),
  }

  if (direction === TravelDirection.OUTBOUND) {
    personalInfo.emiratesId = String(state.emiratesId || '')
    personalInfo.civilIdExpiryDate = String(state.civilIdExpiryDate || '')
  }

  return personalInfo
}

export const useUserStore = defineStore('userStore', {
  state: (): State => {
    const cacheState = getCacheState()

    const initDate = (key: typeof DEPARTURE_DATE_KEY | typeof RETURN_DATE_KEY): Date | null => {
      let date = cacheState[key] || null
      if (!date) {
        return null
      }

      date = new Date(date)
      if (isValidDate(date)) {
        return date
      }

      const today = new Date()
      localStorageCacheService.update(TRAVEL_KEY, key, today)
      return today
    }

    return {
      email: cacheState[EMAIL_KEY] || null,
      phone: cacheState[PHONE_KEY] || null,
      maskedPhone: cacheState[MASKED_PHONE_KEY] || cacheState[PHONE_KEY] || null,
      firstName: cacheState[FIRST_NAME_KEY] || null,
      familyName: cacheState[FAMILY_NAME_KEY] || null,
      dateOfBirth: (() => {
        const date = cacheState[DATE_OF_BIRTH_KEY] || null

        if (!date) return null
        return new Date(date)
      })(),
      nationality: cacheState[NATIONALITY_KEY] || null,
      passportNumber: cacheState[PASSPORT_NUMBER_KEY] || null,
      emiratesId: cacheState[EMIRATES_ID_KEY] || null,
      civilIdExpiryDate: cacheState[CIVIL_ID_EXPIRY_DATE_KEY] || null,
      destination: cacheState[DESTINATION_KEY] || [],
      residenceCountry: cacheState[RESIDENCE_COUNTRY_KEY] || {},
      departureDate: initDate(DEPARTURE_DATE_KEY),
      returnDate: initDate(RETURN_DATE_KEY),
      tripType: cacheState[TRIP_TYPE_KEY] || 'SINGLE',
      prevTripType: cacheState[PREV_TRIP_TYPE_KEY] || cacheState[TRIP_TYPE_KEY] || 'SINGLE',
      adultsCount: cacheState[ADULTS_COUNT_KEY] || 1,
      childrenCount: cacheState[CHILDREN_COUNT_KEY] || 0,
      promocode: cacheState[PROMOCODE_KEY] || null,
      selectedPlan: cacheState[SELECTED_PLAN_KEY] || null,
      withWinterSport: cacheState[WITH_WINTER_SPORT_KEY] || false,
      withCovid: cacheState[WITH_COVID_KEY] || true,
      withCyber: cacheState[WITH_CYBER_KEY] || false,
      countryCode: cacheState[COUNTRY_CODE_KEY] || 'QA',
      lastFlowDirection: cacheState[LAST_FLOW_DIRECTION_KEY],
      activeInsuredTravelersSubPage: undefined,
      paymentMethod: cacheState[PAYMENT_METHOD] || null,
      isAffinity: cacheState[IS_AFFINITY] || false,
      redirectURL: cacheState[REDIRECT_URL] || false,
      isHeroSection: cacheState[IS_HERO_SECTION] || false,
      gender: cacheState[GENDER_KEY] || '1',
      preferredReward: cacheState[PREFERRED_REWARD_KEY] || PreferredCoinsReward.EARN,
    }
  },
  getters: {
    isOneYearPolicySelected: (state) =>
      state.prevTripType === 'SINGLE' && state.tripType === 'MULTI',
  },
  actions: {
    fetchCountries(
      boundType?: CountryBoundType,
      locale?: string,
      name?: string
    ): Promise<Country[]> {
      const api = useApi()
      return api.countriesService.getCountries(boundType, locale, name)
    },

    setDestination(destination: { id: string; name: string }[], withStorage = false): void {
      this.destination = destination

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, DESTINATION_KEY, destination)
      }
    },

    setResidenceCountry(destination: { id: string; name: string }, withStorage = false): void {
      this.residenceCountry = destination

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, RESIDENCE_COUNTRY_KEY, destination)
      }
    },

    setTravelDates(departureDate: Date | null, returnDate: Date | null, withStorage = false): void {
      this.departureDate = departureDate
      this.returnDate = returnDate

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, DEPARTURE_DATE_KEY, departureDate)
        localStorageCacheService.update(TRAVEL_KEY, RETURN_DATE_KEY, returnDate)
      }
    },

    setTripType(tripType: TripType, withStorage = false): void {
      this.tripType = tripType

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, TRIP_TYPE_KEY, tripType)
      }
    },

    setPrevTripType(type: TripType, withStorage = false): void {
      this.prevTripType = type

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, PREV_TRIP_TYPE_KEY, type)
      }
    },

    setTravelersCount(adultsCount: number, childrenCount: number, withStorage = false): void {
      this.adultsCount = adultsCount
      this.childrenCount = childrenCount

      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, ADULTS_COUNT_KEY, adultsCount)
        localStorageCacheService.update(TRAVEL_KEY, CHILDREN_COUNT_KEY, childrenCount)
      }
    },

    setPromocode(promocode: string | null, withStorage = false): void {
      this.promocode = promocode
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, PROMOCODE_KEY, promocode)
      }
    },

    clearPromocode(withStorage = false) {
      this.promocode = null
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, PROMOCODE_KEY, undefined)
      }
    },

    setEmail(email: string | null, withStorage = false): void {
      this.email = email
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, EMAIL_KEY, email)
      }
    },

    setPhone(phone: string | null, withStorage = false): void {
      this.phone = phone
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, PHONE_KEY, phone)
      }
    },

    setMaskedPhone(maskedPhone: string | null, withStorage = false): void {
      this.maskedPhone = maskedPhone
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, MASKED_PHONE_KEY, maskedPhone)
      }
    },

    setSelectedPlan(selectedPlan: string | null, withStorage = false): void {
      this.selectedPlan = selectedPlan
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, SELECTED_PLAN_KEY, selectedPlan)
      }
    },

    setFirstName(firstName: string | null, withStorage = false): void {
      this.firstName = firstName
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, FIRST_NAME_KEY, firstName)
      }
    },

    setFamilyName(familyName: string | null, withStorage = false): void {
      this.familyName = familyName
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, FAMILY_NAME_KEY, familyName)
      }
    },

    setDateOfBirth(dateOfBirth: Date | null, withStorage = false): void {
      this.dateOfBirth = dateOfBirth
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, DATE_OF_BIRTH_KEY, dateOfBirth)
      }
    },

    setNationality(nationality: Nationality | null, withStorage = false): void {
      this.nationality = nationality
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, NATIONALITY_KEY, nationality)
      }
    },

    setEmiratesId(emiratesId: string | null, withStorage = false): void {
      this.emiratesId = emiratesId
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, EMIRATES_ID_KEY, emiratesId)
      }
    },

    setCivilIdExpiryDate(civilIdExpiryDate: Date | null, withStorage = false): void {
      this.civilIdExpiryDate = civilIdExpiryDate
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, CIVIL_ID_EXPIRY_DATE_KEY, civilIdExpiryDate)
      }
    },

    setPassportNumber(passportNumber: string | null, withStorage = false): void {
      this.passportNumber = passportNumber
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, PASSPORT_NUMBER_KEY, passportNumber)
      }
    },

    setWithWinterSport(withWinterSport: boolean, withStorage = false): void {
      this.withWinterSport = withWinterSport
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, WITH_WINTER_SPORT_KEY, withWinterSport)
      }
    },

    setwithCovid(withCovid: boolean, withStorage = false): void {
      this.withCovid = withCovid
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, WITH_COVID_KEY, withCovid)
      }
    },

    setWithCyber(withCyber: boolean, withStorage = false): void {
      this.withCyber = withCyber
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, WITH_CYBER_KEY, withCyber)
      }
    },

    setCountryCode(countryCode: string, withStorage = false): void {
      this.countryCode = countryCode
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, COUNTRY_CODE_KEY, countryCode)
      }
    },

    setLastFlowDirection(lastFlowDirection: TravelDirection, withStorage = false): void {
      this.lastFlowDirection = lastFlowDirection
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, LAST_FLOW_DIRECTION_KEY, lastFlowDirection)
      }
    },

    setPaymentMethod(method: string, withStorage = false) {
      this.paymentMethod = method
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, PAYMENT_METHOD, method)
      }
    },

    setActiveInsuredTravelersSubPage(activeSubPage: 'edit' | 'add' | undefined): void {
      this.activeInsuredTravelersSubPage = activeSubPage
    },

    setAffinity(isAffinity: boolean, withStorage = true) {
      this.isAffinity = isAffinity
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, IS_AFFINITY, isAffinity)
      }
    },

    setRedirectURL(url: string | null, withStorage = true) {
      this.redirectURL = url
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, REDIRECT_URL, url)
      }
    },

    setIsHeroSection(isHeroSection: boolean, withStorage = true) {
      this.isHeroSection = isHeroSection
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, IS_HERO_SECTION, isHeroSection)
      }
    },

    setGender(gender: string | null, withStorage = true) {
      this.gender = gender
      if (withStorage) {
        sessionStorageCacheService.update(TRAVEL_KEY, GENDER_KEY, gender)
      }
    },

    setPreferredReward(preferredReward: PreferredCoinsReward, withStorage = true) {
      this.preferredReward = preferredReward
      if (withStorage) {
        localStorageCacheService.update(TRAVEL_KEY, PREFERRED_REWARD_KEY, preferredReward)
      }
    },

    async prefillUserDataByAuth(
      accessToken: string,
      csrfToken: string,
      sourceId: string,
      language: string
    ) {
      const api = useApi()
      try {
        const userInfo = await api.userService.getUserInfo(
          accessToken,
          csrfToken,
          sourceId,
          language
        )

        const qatarRegion = userInfo.regionData?.find((region) => region.region === 'Qatar')
        if (qatarRegion) {
          this.setEmiratesId(qatarRegion.civilId, true)
        }

        if (!this.firstName && userInfo.firstName) this.setFirstName(userInfo.firstName, true)
        if (!this.familyName && userInfo.lastName) this.setFamilyName(userInfo.lastName, true)
        if (!this.dateOfBirth && userInfo.dateOfBirth)
          this.setDateOfBirth(parseDateFromStr(userInfo.dateOfBirth), true)
        if (!this.passportNumber && userInfo.passportNo)
          this.setPassportNumber(userInfo.passportNo, true)
        if (!this.phone && userInfo.phone) this.setPhone(userInfo.phone, true)
        if (!this.email && userInfo.email) this.setEmail(userInfo.email, true)
      } catch (error) {
        console.error('Failed to prefill user data:', error)
      }
    },

    // eslint-disable-next-line complexity
    setFormData(data: FormDataResponse, direction: TravelDirection, isFullFields = false) {
      const phone = data.phone?.length === 8 ? `974${data.phone}` : data.phone
      const maskedPhone = phone ? applyPhoneMask(phone, this.countryCode || 'QA') : ''
      const dateOfBirth = new Date(parseDateFromStr(data.birth_date ?? ''))

      this.setPhone(phone, true)
      this.setMaskedPhone(maskedPhone, true)
      this.setEmail(data.email, true)
      if (isFullFields) {
        if (direction === TravelDirection.OUTBOUND) {
          this.setEmiratesId(data.qatar_id ?? '', true)
          this.setCivilIdExpiryDate(new Date(data.civil_id_expiry_date ?? ''), true)
        }
        this.setFirstName(data.first_name, true)
        this.setFamilyName(data.last_name, true)
        this.setGender(String(data.gender), true)
        this.setDateOfBirth(dateOfBirth, true)
        this.setPassportNumber(data.passport_number ?? '', true)
        this.setNationality(
          {
            nationalityCode: data.nationalityCode ?? '',
            nationalityName: data.nationalityName ?? '',
          },
          true
        )
      }
    },

    async getFormData(direction: TravelDirection, isFullFields = false) {
      const info = getInfo(this, direction)
      const requiredFields = [...REQUIRED_FIELDS]

      if (isFullFields) {
        requiredFields.push(...PERSONAL_FIELDS)
        if (direction === TravelDirection.OUTBOUND) {
          requiredFields.push(...OUTBOUND_PERSONAL_FIELDS)
        }
      }

      const hasAllFields = requiredFields.every((field) => info[field])

      if (hasAllFields) return true

      const api = useApi()

      try {
        const resp = await api.userService.getFormData(direction, isFullFields)
        if (!resp) return false
        this.setFormData(resp, direction, isFullFields)
        return true
      } catch (error) {
        console.error('Failed to get form data:', error)
        return false
      }
    },
  },
})
